import { useEffect, useRef, useState } from 'react';
import { CaretDown, ChatCircleDots } from '@phosphor-icons/react';
import { CHAT_ESCAPE_MESSAGE, CHAT_URL, OPEN_CHAT_EVENT } from '../../content/site';
import '../LandingPage.css';

function ChatWidget() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  // The iframe is only mounted on first open (it fetches a greeting), then
  // kept mounted so the conversation survives minimising.
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const minimizeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  const openChat = () => {
    setHasOpenedChat(true);
    setIsChatOpen(true);
  };

  const minimizeChat = () => setIsChatOpen(false);

  // Other components (e.g. the contact section) can ask the widget to open.
  useEffect(() => {
    window.addEventListener(OPEN_CHAT_EVENT, openChat);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, openChat);
  }, []);

  // Move focus into the panel on open and back to the launcher on minimise.
  useEffect(() => {
    if (isChatOpen) {
      minimizeRef.current?.focus();
    } else if (wasOpen.current) {
      launcherRef.current?.focus();
    }
    wasOpen.current = isChatOpen;
  }, [isChatOpen]);

  // Escape minimises the chat, whether focus is on this page or inside the
  // same-origin chat iframe (which posts CHAT_ESCAPE_MESSAGE to its parent).
  useEffect(() => {
    if (!isChatOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsChatOpen(false);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data?.type === CHAT_ESCAPE_MESSAGE) {
        setIsChatOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('message', onMessage);
    };
  }, [isChatOpen]);

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        className={`chat-widget-icon ${isChatOpen ? 'chat-widget-icon--hidden' : ''}`}
        onClick={openChat}
        aria-label="Open chat with EFSoft"
        aria-expanded={isChatOpen}
        aria-controls="chat-widget-window"
      >
        <ChatCircleDots size={28} weight="fill" aria-hidden="true" />
      </button>

      <div
        id="chat-widget-window"
        className={`chat-widget-window ${!isChatOpen ? 'chat-widget-window--hidden' : ''}`}
        role="dialog"
        aria-labelledby="chat-widget-title"
      >
        <div className="chat-widget-header">
          <span id="chat-widget-title">Chat with EFSoft</span>
          <button
            ref={minimizeRef}
            type="button"
            className="chat-widget-minimize-button"
            onClick={minimizeChat}
            aria-label="Minimise chat"
            title="Minimise chat"
          >
            <CaretDown size={20} weight="bold" aria-hidden="true" />
          </button>
        </div>
        <div className="chat-widget-content" aria-busy={hasOpenedChat && !isFrameLoaded}>
          {hasOpenedChat && !isFrameLoaded && (
            <div className="chat-skeleton" aria-hidden="true">
              <span className="chat-skeleton-bubble" />
              <span className="chat-skeleton-bubble chat-skeleton-bubble--short" />
              <span className="chat-skeleton-input" />
            </div>
          )}
          {hasOpenedChat && (
            <iframe
              src={CHAT_URL}
              className="chat-widget-iframe"
              title="EFSoft chat"
              onLoad={() => setIsFrameLoaded(true)}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default ChatWidget;
